import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import { Clock, Check, X, CalendarClock, Mail, Phone, MessageSquare, Plus, Trash2, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${hour}:${String(m).padStart(2, '0')} ${period}`;
};
const fmtDate = (s) => { try { return new Date(s + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }); } catch { return s; } };

const STATUS_STYLES = {
  pending: { bg: '#FEF3C7', color: '#B45309', label: 'Pending' },
  approved: { bg: '#DCFCE7', color: '#15803D', label: 'Approved' },
  denied: { bg: '#FEE2E2', color: '#B91C1C', label: 'Denied' },
};

export default function AdminTimeRequests() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = () => {
    setLoading(true);
    axios.get(`${API_URL}/api/admin/time-requests`, { withCredentials: true })
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const act = async (id, decision) => {
    if (decision === 'deny' && !window.confirm('Deny this request and email the client?')) return;
    setBusy(id);
    try {
      await axios.post(`${API_URL}/api/admin/time-requests/${id}/${decision}`, {}, { withCredentials: true });
      toast.success(decision === 'approve' ? 'Approved — session booked & client emailed' : 'Request denied — client notified');
      window.dispatchEvent(new Event('time-requests-updated'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(null);
    }
  };

  const pending = rows.filter((r) => r.status === 'pending');
  const decided = rows.filter((r) => r.status !== 'pending');

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="time-requests-page">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0F1C] flex items-center gap-2"><CalendarClock size={22} /> Custom Time Requests</h1>
          <p className="text-sm text-[#475569]">Clients asking for sessions outside normal business hours. Approve to auto-create the Zoom booking + email them; deny to send a polite "pick another time" note.</p>
        </div>

        <SmsGatewayCard />

        {loading ? (
          <div className="py-20 flex justify-center"><div className="w-10 h-10 border-4 border-[#94A3B8] border-t-transparent rounded-full animate-spin"></div></div>
        ) : rows.length === 0 ? (
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-10 text-center text-[#94A3B8]" data-testid="time-requests-empty">No time requests yet.</div>
        ) : (
          <>
            <Section title={`Pending (${pending.length})`} testid="tr-pending">
              {pending.length === 0 ? <p className="text-sm text-[#94A3B8]">Nothing waiting on you.</p> :
                pending.map((r) => <RequestCard key={r.request_id} r={r} busy={busy} act={act} />)}
            </Section>
            {decided.length > 0 && (
              <Section title={`History (${decided.length})`} testid="tr-history">
                {decided.map((r) => <RequestCard key={r.request_id} r={r} busy={busy} act={act} />)}
              </Section>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}

const Section = ({ title, testid, children }) => (
  <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid={testid}>
    <h2 className="font-bold text-[#0A0F1C] mb-4 flex items-center gap-2"><Clock size={16} /> {title}</h2>
    <div className="space-y-3">{children}</div>
  </div>
);

const RequestCard = ({ r, busy, act }) => {
  const s = STATUS_STYLES[r.status] || STATUS_STYLES.pending;
  return (
    <div className="border border-[#E2E8F0] rounded-lg p-4" data-testid={`tr-row-${r.request_id}`}>
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-bold text-[#0A0F1C]">{r.name}</span>
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: s.bg, color: s.color }}>{s.label}</span>
          </div>
          <div className="text-xs text-[#475569] mt-1 flex items-center gap-3 flex-wrap">
            <span className="flex items-center gap-1"><Mail size={12} /> {r.email}</span>
            {r.phone && <span className="flex items-center gap-1"><Phone size={12} /> {r.phone}</span>}
          </div>
          <div className="text-sm font-semibold text-[#0A0F1C] mt-2">{r.service_name} · {r.brand_name}</div>
          <div className="text-sm text-[#1D4ED8] font-bold flex items-center gap-1 mt-0.5"><CalendarClock size={14} /> {fmtDate(r.preferred_date)} at {fmtTime(r.preferred_time)}</div>
          {r.notes && <div className="text-sm text-[#475569] mt-2"><strong>Notes:</strong> {r.notes}</div>}
          {r.intake_responses && Object.keys(r.intake_responses).length > 0 && (
            <details className="mt-2">
              <summary className="text-xs font-semibold text-[#475569] cursor-pointer">Intake answers</summary>
              <div className="mt-2 space-y-1.5">
                {Object.entries(r.intake_responses).map(([k, v]) => (
                  <div key={k} className="text-xs"><span className="text-[#94A3B8] uppercase tracking-wide">{k.replace(/_/g, ' ')}:</span> <span className="text-[#0A0F1C]">{v}</span></div>
                ))}
              </div>
            </details>
          )}
        </div>
        {r.status === 'pending' && (
          <div className="flex gap-2">
            <button onClick={() => act(r.request_id, 'approve')} disabled={busy === r.request_id} className="inline-flex items-center gap-1 bg-[#059669] hover:bg-[#047857] text-white text-sm font-bold px-3 py-2 rounded-lg disabled:opacity-50" data-testid={`tr-approve-${r.request_id}`}>
              <Check size={14} /> Approve & Book
            </button>
            <button onClick={() => act(r.request_id, 'deny')} disabled={busy === r.request_id} className="inline-flex items-center gap-1 bg-white border-2 border-[#FECACA] text-[#DC2626] hover:bg-[#FEF2F2] text-sm font-bold px-3 py-2 rounded-lg disabled:opacity-50" data-testid={`tr-deny-${r.request_id}`}>
              <X size={14} /> Deny
            </button>
          </div>
        )}
        {r.booking_id && <div className="text-[11px] text-[#94A3B8] font-mono self-center">{r.booking_id}</div>}
      </div>
    </div>
  );
};

const CARRIER_LABELS = {
  verizon: 'Verizon', att: 'AT&T', tmobile: 'T-Mobile', sprint: 'Sprint',
  boost: 'Boost Mobile', cricket: 'Cricket', uscellular: 'U.S. Cellular',
  metropcs: 'Metro by T-Mobile', googlefi: 'Google Fi', virgin: 'Virgin Mobile',
  ting: 'Ting', consumercellular: 'Consumer Cellular', straighttalk: 'Straight Talk', mint: 'Mint Mobile',
};

const SmsGatewayCard = () => {
  const [cfg, setCfg] = useState(null);
  const [carriers, setCarriers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/sms-gateway`, { withCredentials: true })
      .then((r) => { setCfg(r.data.config); setCarriers(r.data.carriers || []); })
      .catch(() => setCfg({ enabled: false, notify_time_request: true, notify_booking: false, recipients: [] }));
  }, []);

  if (!cfg) return null;

  const update = (patch) => setCfg({ ...cfg, ...patch });
  const updateRecip = (i, patch) => {
    const recipients = cfg.recipients.map((r, idx) => idx === i ? { ...r, ...patch } : r);
    setCfg({ ...cfg, recipients });
  };
  const addRecip = () => setCfg({ ...cfg, recipients: [...cfg.recipients, { label: '', phone: '', carrier: carriers[0] || 'verizon' }] });
  const removeRecip = (i) => setCfg({ ...cfg, recipients: cfg.recipients.filter((_, idx) => idx !== i) });

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/sms-gateway`, cfg, { withCredentials: true });
      toast.success('SMS alert settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/sms-gateway/test`, {}, { withCredentials: true });
      toast.success(r.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test failed');
    } finally { setTesting(false); }
  };

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="sms-gateway-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-bold text-[#0A0F1C] flex items-center gap-2"><MessageSquare size={16} /> Text-message alerts (free, no Twilio)</h2>
          <p className="text-xs text-[#475569] mt-1 max-w-xl">Get a text the moment a request or booking comes in, sent through your carrier's free email-to-SMS gateway. Delivery is carrier-dependent and can occasionally be delayed or dropped, so keep email alerts on too.</p>
        </div>
        <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer" data-testid="sms-enabled-toggle">
          <input type="checkbox" checked={cfg.enabled} onChange={(e) => update({ enabled: e.target.checked })} className="w-4 h-4" />
          {cfg.enabled ? 'Enabled' : 'Disabled'}
        </label>
      </div>

      <div className="mt-4 flex gap-5 flex-wrap">
        <label className="flex items-center gap-2 text-sm text-[#475569] cursor-pointer">
          <input type="checkbox" checked={cfg.notify_time_request} onChange={(e) => update({ notify_time_request: e.target.checked })} data-testid="sms-notify-timereq" /> Text me on custom time requests
        </label>
        <label className="flex items-center gap-2 text-sm text-[#475569] cursor-pointer">
          <input type="checkbox" checked={cfg.notify_booking} onChange={(e) => update({ notify_booking: e.target.checked })} data-testid="sms-notify-booking" /> Text me on new bookings
        </label>
      </div>

      <div className="mt-4 space-y-2">
        <div className="text-xs font-bold text-[#94A3B8] uppercase tracking-wide">Recipients</div>
        {cfg.recipients.length === 0 && <p className="text-sm text-[#94A3B8]">No phone numbers yet — add one below.</p>}
        {cfg.recipients.map((r, i) => (
          <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1.2fr_1.4fr_auto] gap-2 items-center" data-testid={`sms-recipient-${i}`}>
            <input type="text" placeholder="Label (e.g. David)" value={r.label || ''} onChange={(e) => updateRecip(i, { label: e.target.value })} className="px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
            <input type="tel" placeholder="10-digit phone" value={r.phone || ''} onChange={(e) => updateRecip(i, { phone: e.target.value })} className="px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" data-testid={`sms-recipient-phone-${i}`} />
            <select value={r.carrier} onChange={(e) => updateRecip(i, { carrier: e.target.value })} className="px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm bg-white" data-testid={`sms-recipient-carrier-${i}`}>
              {carriers.map((c) => <option key={c} value={c}>{CARRIER_LABELS[c] || c}</option>)}
            </select>
            <button onClick={() => removeRecip(i)} className="text-[#DC2626] hover:bg-[#FEF2F2] p-2 rounded-lg justify-self-start" data-testid={`sms-recipient-remove-${i}`}><Trash2 size={16} /></button>
          </div>
        ))}
        <button onClick={addRecip} className="inline-flex items-center gap-1 text-sm font-semibold text-[#1D4ED8] hover:underline mt-1" data-testid="sms-add-recipient"><Plus size={14} /> Add a phone number</button>
      </div>

      <div className="mt-4 flex gap-2 flex-wrap">
        <button onClick={save} disabled={saving} className="bg-[#1D4ED8] hover:bg-[#1e40af] text-white text-sm font-bold px-4 py-2 rounded-lg disabled:opacity-50" data-testid="sms-save-btn">{saving ? 'Saving…' : 'Save settings'}</button>
        <button onClick={sendTest} disabled={testing || !cfg.enabled} className="inline-flex items-center gap-1 bg-white border border-[#E2E8F0] text-[#0A0F1C] hover:bg-[#F8FAFC] text-sm font-bold px-4 py-2 rounded-lg disabled:opacity-50" data-testid="sms-test-btn"><Send size={14} /> {testing ? 'Sending…' : 'Send test text'}</button>
      </div>
      <p className="text-[11px] text-[#94A3B8] mt-2">Tip: Save first, then send a test. If it doesn't arrive within a few minutes, double-check the carrier — gateways vary by plan/region.</p>
    </div>
  );
};

